/**
 * Monatserfassung der Lohnverrechnung (AP-22-D1) — der Modus „nur
 * Lohnverrechnung": Die handschriftliche Monatsliste wird Tag für Tag
 * abgetippt, und die berechneten Spalten stehen sofort daneben.
 *
 * **Gerechnet wird im Browser, mit demselben Kern wie am Server**
 * (`rechneMonat` aus `@symphenia/payroll-core`). Der Server liefert nur den
 * Eingang: Zeilen, Arbeitszeitmodell, Einstellungen zum Stichtag, Feiertage,
 * Fehlzeit-Arten und den Übertrag der Vormonate. So sieht die Maske nach jedem
 * Feld dieselben Zahlen, die später die Auswertung zeigt — ohne Rundreise.
 *
 * **Kein Listen-Standard, sondern eine Eingabemaske.** Die Seite wird gepflegt,
 * nicht gefiltert oder sortiert: 28–31 feste Zeilen, eine je Tag, in der
 * Reihenfolge des Kalenders. Eine FilterBar hätte hier nichts zu tun (Abgrenzung:
 * Sitzungsprotokoll 21.9.2026, „gelesen oder gepflegt").
 *
 * **Die Spalten gehören trotzdem dem Benutzer (AP-22 Teil 5):** Reihenfolge und Auswahl
 * stehen in `lib/raster-spalten.ts` und liegen in der Ablage der Listen-Layouts — je
 * Benutzer, mit Firmenstandard. Kopf, Tageszeile und Summenzeile zeichnen dieselbe Liste.
 *
 * **Speichern (AP-36, Fall 1):** Jede Zeile ist eine Speicherstelle
 * (`payrollDay:<userId>:<datum>`), jedes Feld speichert beim Verlassen. Ein Tag
 * ohne Zeile entsteht mit dem ersten Feld; wird er ganz geleert, löscht ihn der
 * Server. Keine Gruppe: Jedes Feld ist für sich gültig, und die Rechnung soll
 * nach jedem Feld nachziehen, nicht erst beim Verlassen der Zeile.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Info, Pencil, Plus, Settings, X } from 'lucide-react';
import { erfuelltRecht, geldText } from '@symphenia/shared-types';
import { rechneMonat, type MonatsErgebnis, type TagErgebnis, kvVorlage, zulageSchluessel, type KvVorlage } from '@symphenia/payroll-core';
import { api } from '@/lib/axios';
import { useAuthStore } from '@/lib/auth.store';
import { pageWide, selectBase } from '@/lib/form-styles';
import {
  FeldStatusZeichen,
  mitStand,
  leseZahl,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Feld,
  type Speicherstelle,
  type Status,
} from '@/lib/speichern';
import { ExportButton } from '@/components/ui/ExportButton';
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions';
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings';
import {
  fetchPayrollEmployees,
  fetchPayrollMonth,
  monthsExportRequest,
  type MonatsDatei,
  type PayrollMonth,
} from '../api/payroll.api';
import type { DayEntry } from '../types/payroll.types';
import { leseUhrzeit, pruefeUhrzeit } from '../lib/uhrzeit';
import { rasterTaste } from '../lib/raster-tasten';
import {
  RASTER_LAYOUT_KEY,
  alleRasterSpalten,
  blendeAus,
  blendeEin,
  rasterLayoutAus,
  sichtbareSpalten,
  versteckteSpalten,
  verschiebeSpalte,
  type RasterLayout,
  type RasterSpalte,
  type RasterSpaltenKey,
} from '../lib/raster-spalten';
import { FertigstellenDialog } from '../components/FertigstellenDialog';
import { AendernDialog, Aenderungsprotokoll } from '../components/MonatAendern';
import { WegUndZettel, ZettelAnsicht } from '../components/Abgabezettel';
import { ZettelVorschlag } from '../components/ZettelVorschlag';

// ── Hilfen ──────────────────────────────────────────────────────────────────

const KURZ_TAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const STUFE = { klein: 'klein', mittel: 'mittel', gross: 'groß' } as const;

function zpad(n: number): string {
  return String(n).padStart(2, '0');
}

function heutigerMonat(): string {
  const d = new Date();
  return `${d.getFullYear()}-${zpad(d.getMonth() + 1)}`;
}

function verschiebe(monat: string, um: number): string {
  const [j, m] = monat.split('-').map(Number);
  const d = new Date(Date.UTC(j, m - 1 + um, 1));
  return `${d.getUTCFullYear()}-${zpad(d.getUTCMonth() + 1)}`;
}

function tageDes(jahr: number, monat: number): string[] {
  const n = new Date(Date.UTC(jahr, monat, 0)).getUTCDate();
  return Array.from({ length: n }, (_, i) => `${jahr}-${zpad(monat)}-${zpad(i + 1)}`);
}

const MONATSNAMEN = [
  'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/** Stunden dezimal mit Komma; 0 bleibt im Raster leer, damit das Auge die Werte findet. */
function std(n: number | undefined, leerBeiNull = true): string {
  if (n === undefined || (leerBeiNull && Math.abs(n) < 0.005)) return '';
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Der auffälligste Zustand der Felder einer Zeile — für eine Anzeige am Zeilenende. */
function zeilenStatus(felder: Status[]): Status {
  const fehler = felder.find((f) => f.fehler);
  if (fehler) return { zustand: 'fehler', fehler: fehler.fehler };
  if (felder.some((f) => f.zustand === 'speichert')) return { zustand: 'speichert', fehler: null };
  if (felder.some((f) => f.zustand === 'gespeichert')) return { zustand: 'gespeichert', fehler: null };
  return { zustand: 'ruhig', fehler: null };
}

interface Mitarbeiter {
  id: string;
  email: string | null;
  noPayroll?: boolean;
  profile?: { firstName?: string | null; lastName?: string | null } | null;
}

/** „01.10.2026, 14:05" — Zeitpunkt des Fertigstellens. */
function datumZeit(iso: string): string {
  const d = new Date(iso);
  return `${zpad(d.getDate())}.${zpad(d.getMonth() + 1)}.${d.getFullYear()}, ${zpad(d.getHours())}:${zpad(d.getMinutes())}`;
}

function nameVon(u: Mitarbeiter): string {
  return [u.profile?.lastName, u.profile?.firstName].filter(Boolean).join(' ') || u.email || '—';
}

// ── Seite ───────────────────────────────────────────────────────────────────

export default function PayrollEntryPage() {
  const authUser = useAuthStore((s) => s.user);
  const darfErfassen = erfuelltRecht('payroll.day_entry.manage', authUser?.permissions);
  const darfFertigstellen = erfuelltRecht('payroll.month.finalize', authUser?.permissions);
  const [fertigstellenOffen, setFertigstellenOffen] = useState(false);
  // G2: der eröffnete Änderungsvorgang am fertiggestellten Monat (Modus „mit Grund").
  const [aendernOffen, setAendernOffen] = useState(false);
  const [vorgang, setVorgang] = useState<{ id: string; reason: string | null } | null>(null);
  // H: der Abgabezettel, der gerade neben dem Raster steht.
  const [zettel, setZettel] = useState<{ offen: string | null; dateien: MonatsDatei[] }>({ offen: null, dateien: [] });
  // I: der Zettel, dessen gelesener Vorschlag gerade über dem Raster steht — das Blatt links daneben (Teil 6, V).
  const [vorschlag, setVorschlag] = useState<string | null>(null);
  // Teil 4: die NLZ des Monats nach Art — unter der Summenzeile aufklappbar.
  const [nlzSummeOffen, setNlzSummeOffen] = useState(false);
  const darfZettelLesen = erfuelltRecht('payroll.slip.read', authUser?.permissions);
  const darfZettelPflegen = erfuelltRecht('payroll.slip.manage', authUser?.permissions);
  const [params, setParams] = useSearchParams();
  const userId = params.get('user') ?? '';
  const monatText = /^\d{4}-\d{2}$/.test(params.get('month') ?? '') ? params.get('month')! : heutigerMonat();
  const [jahr, monat] = monatText.split('-').map(Number);

  const setze = (werte: { user?: string; month?: string }) => {
    const neu = new URLSearchParams(params);
    if (werte.user !== undefined) neu.set('user', werte.user);
    if (werte.month !== undefined) neu.set('month', werte.month);
    setParams(neu, { replace: true });
  };

  const [mitarbeiter, setMitarbeiter] = useState<Mitarbeiter[]>([]);
  const [daten, setDaten] = useState<PayrollMonth | null>(null);
  const [zeilen, setZeilen] = useState<Record<string, DayEntry>>({});
  const [laedt, setLaedt] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // AP-38-D: die eigene Mitarbeiterliste der Lohnverrechnung — ohne Externe
    // und ohne `core.user.read`.
    fetchPayrollEmployees()
      .then((liste0) => {
        const liste = liste0.slice().sort((a, b) => nameVon(a).localeCompare(nameVon(b), 'de'));
        setMitarbeiter(liste);
      })
      .catch(() => setMitarbeiter([]));
  }, []);

  const uebernimm = useCallback(
    (m: PayrollMonth) => {
      setDaten(m);
      const neu: Record<string, DayEntry> = {};
      for (const tag of tageDes(jahr, monat)) {
        neu[tag] = m.zeilen.find((z) => z.date === tag) ?? leererTag(userId, tag);
      }
      setZeilen(neu);
    },
    [userId, jahr, monat],
  );

  const laden = useCallback(async () => {
    if (!userId) {
      setDaten(null);
      return;
    }
    setLaedt(true);
    setError(null);
    try {
      uebernimm(await fetchPayrollMonth(userId, jahr, monat));
    } catch (err) {
      console.error('Failed to load payroll month', err);
      setDaten(null);
      setError('Der Monat konnte nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, [userId, jahr, monat, uebernimm]);

  useEffect(() => {
    void laden();
  }, [laden]);

  // Ein Vorgang gilt für einen Mitarbeiter und einen Monat — beim Wechsel endet er.
  useEffect(() => {
    setVorgang(null);
    setZettel({ offen: null, dateien: [] });
    setVorschlag(null);
  }, [userId, jahr, monat]);

  // AP-22 Teil 2: Ein fertiggestellter Lohnmonat ist eingefroren. Ob er sich
  // ändern lässt, sagt die Einstellung des Mandanten (G2): gesperrt · mit Grund
  // (nur in einem eröffneten Vorgang) · frei.
  const fertig = daten?.abschluss.status === 'FINAL';
  const modus = daten?.abschluss.bearbeitung ?? 'LOCKED';
  const darfAendern = darfErfassen && darfFertigstellen;
  const aenderbar = fertig && darfAendern && (modus === 'FREE' || (modus === 'REASON' && vorgang !== null));

  // Nach einer Änderung im fertiggestellten Monat friert der Server neu ein —
  // das Ergebnis kommt von dort, die Zeilen bleiben, wie die Maske sie hat.
  const nachAenderung = useCallback(async () => {
    try {
      setDaten(await fetchPayrollMonth(userId, jahr, monat));
    } catch {
      // Die Zeile ist gespeichert; das neue Ergebnis kommt mit dem nächsten Laden.
    }
  }, [userId, jahr, monat]);

  const gespeichert = useCallback(
    (z: DayEntry) => {
      setZeilen((alt) => ({ ...alt, [z.date]: z }));
      if (fertig) void nachAenderung();
    },
    [fertig, nachAenderung],
  );

  // Der eine Rechenweg — dieselbe Funktion, mit der der Server rechnet. Ein
  // fertiggestellter Monat wird nicht neu gerechnet: Er zeigt, was eingefroren
  // wurde, auch wenn sich die Rechenregeln seither geändert haben.
  const ergebnis: MonatsErgebnis | null = useMemo(() => {
    if (!daten) return null;
    if (daten.abschluss.status === 'FINAL' && daten.ergebnis) return daten.ergebnis;
    const erfasst = Object.values(zeilen).filter((z) => z.updatedAt !== null);
    return rechneMonat({ ...daten, zeilen: erfasst });
  }, [daten, zeilen]);

  const feiertage = useMemo(() => new Map((daten?.feiertage ?? []).map((f) => [f.date, f.name])), [daten]);

  // AP-22 Teil 5: Spalten nach Wahl — eigene Einstellung, sonst Firmenstandard, sonst Auslieferungszustand.
  const [layout, setLayout] = useState<RasterLayout>(() => rasterLayoutAus(loadUiSetting<unknown>(RASTER_LAYOUT_KEY, null)));
  const [spaltenModus, setSpaltenModus] = useState(false);
  const [zielSpalte, setZielSpalte] = useState<RasterSpaltenKey | null>(null);
  const gezogen = useRef<RasterSpaltenKey | null>(null);
  // Teil 6, K3: Zu den festen Spalten kommen die der eingeschalteten Zulagen — aus dem Katalog der
  // KV-Vorlage dieses Monats. Ein vor Teil 6 fertiggestellter Monat trägt keine Vorlage und zeigt keine.
  const vorlage = useMemo(() => (daten?.vorlage ? kvVorlage(daten.vorlage) : null), [daten?.vorlage]);
  const alleSpalten = useMemo(() => alleRasterSpalten(vorlage, daten?.werte), [vorlage, daten?.werte]);
  const spalten = useMemo(() => sichtbareSpalten(layout, alleSpalten), [layout, alleSpalten]);
  const versteckt = useMemo(() => versteckteSpalten(layout, alleSpalten), [layout, alleSpalten]);
  const aendereLayout = (neu: RasterLayout) => {
    if (neu === layout) return;
    saveUiSetting(RASTER_LAYOUT_KEY, neu);
    setLayout(neu);
  };
  /** Tag vorn, Status hinten — für die Zeilen, die über die ganze Breite gehen. */
  const breite = spalten.length + 2;
  const aktiveArten = useMemo(() => (daten?.arten ?? []).filter((a) => a.isActive), [daten]);

  return (
    <div className={pageWide}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Monatserfassung</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Tage aus der Monatsliste erfassen. Jedes Feld wird gespeichert, sobald du es verlässt; die
            berechneten Spalten ziehen sofort nach. Mit den Pfeiltasten und Enter wechselst du wie in Excel
            zwischen den Feldern.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Mitarbeiter"
            className={`${selectBase} w-64 py-1.5 text-sm`}
            value={userId}
            onChange={(e) => setze({ user: e.target.value })}
          >
            <option value="">Mitarbeiter wählen …</option>
            {/* Ohne Lohnverrechnung nur, wenn er schon gewählt ist (Verweis aus der Übersicht). */}
            {mitarbeiter.filter((u) => !u.noPayroll || u.id === userId).map((u) => (
              <option key={u.id} value={u.id}>
                {nameVon(u)}
              </option>
            ))}
          </select>
          {userId && daten && !fertig && darfFertigstellen && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              onClick={() => setFertigstellenOffen(true)}
            >
              <CheckCircle2 size={15} aria-hidden="true" />
              Fertigstellen
            </button>
          )}
          {userId && fertig && (
            <ExportButton
              defaultFilename="Zeitauswertung"
              showOrientation={false}
              request={(format) => monthsExportRequest({ from: monatText, userIds: [userId], format })}
              mail={{
                source: 'EXPORT_PAYROLL_MONTHLY',
                title: 'Monatsauswertung',
                subject: `Monatsauswertung ${MONATSNAMEN[monat - 1]} ${jahr}`,
              }}
            />
          )}
          <div className="flex items-center rounded-md border border-border">
            <button
              type="button"
              className="rounded-l-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => setze({ month: verschiebe(monatText, -1) })}
              aria-label="Vormonat"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="min-w-[8.5rem] text-center text-sm font-medium tabular-nums">
              {MONATSNAMEN[monat - 1]} {jahr}
            </span>
            <button
              type="button"
              className="rounded-r-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => setze({ month: verschiebe(monatText, 1) })}
              aria-label="Folgemonat"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {error && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

      {!userId && (
        <p className="rounded-md border border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
          Wähle oben einen Mitarbeiter, um seinen Monat zu erfassen.
        </p>
      )}

      {daten && fertig && (
        <div className="flex items-start gap-2 rounded-md border border-green-600/30 bg-green-600/10 px-4 py-3 text-sm text-foreground">
          <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" />
          <span>
            <strong>Fertiggestellt</strong>
            {daten.abschluss.finalizedAt ? ` am ${datumZeit(daten.abschluss.finalizedAt)}` : ''}
            {daten.abschluss.finalizedBy ? ` von ${daten.abschluss.finalizedBy}` : ''}. Die Zahlen sind
            festgehalten, der Monat lässt sich exportieren.{' '}
            {modus === 'LOCKED' && 'Er kann nicht mehr geändert werden.'}
            {modus === 'REASON' && !vorgang && 'Ändern geht nur mit einem Grund.'}
            {modus === 'FREE' && 'Änderungen werden festgehalten.'}
            {daten.abschluss.changedAfterFinalAt
              ? ` Zuletzt geändert am ${datumZeit(daten.abschluss.changedAfterFinalAt)}.`
              : ''}
          </span>
          {modus === 'REASON' && darfAendern && !vorgang && (
            <button
              type="button"
              className="ml-auto inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1 text-sm text-foreground hover:bg-muted"
              onClick={() => setAendernOffen(true)}
            >
              <Pencil size={14} aria-hidden="true" />
              Monat ändern
            </button>
          )}
        </div>
      )}

      {daten && fertig && vorgang && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-foreground">
          <Pencil size={16} className="mt-0.5 flex-shrink-0 text-amber-600" aria-hidden="true" />
          <span>
            <strong>Änderung läuft</strong>
            {vorgang.reason ? ` — ${vorgang.reason}` : ''}. Jedes Feld wird gespeichert, sobald du es verlässt, und
            mit altem und neuem Wert festgehalten.
          </span>
          <button
            type="button"
            className="ml-auto flex-shrink-0 rounded-md border border-border bg-background px-3 py-1 text-sm text-foreground hover:bg-muted"
            onClick={() => setVorgang(null)}
          >
            Änderung beenden
          </button>
        </div>
      )}

      {userId && daten && (
        <WegUndZettel
          key={`${userId}:${monatText}`}
          userId={userId}
          jahr={jahr}
          monat={monat}
          weg={daten.abschluss.weg}
          uebernahme={daten.abschluss.uebernahme}
          fertig={fertig}
          darfSchreiben={darfErfassen && (!fertig || aenderbar)}
          darfZettelLesen={darfZettelLesen}
          darfZettelPflegen={darfZettelPflegen}
          aenderung={fertig ? vorgang?.id : undefined}
          offen={zettel.offen}
          onOeffnen={(offen, dateien) => setZettel({ offen, dateien })}
          onWeg={(weg) => setDaten((d) => (d ? { ...d, abschluss: { ...d.abschluss, weg } } : d))}
          onGeaendert={() => {
            if (fertig) void nachAenderung();
          }}
          vorschlag={vorschlag}
          onVorschlag={(id, dateien) => {
            setVorschlag(id);
            // Das Blatt steht links neben dem Vorschlag — neben dem Raster braucht es dann nicht noch einmal zu stehen.
            setZettel((z) => (id ? { offen: null, dateien } : { ...z, dateien }));
          }}
        />
      )}

      {userId && daten && ergebnis && vorschlag && (
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <ZettelAnsicht
            userId={userId}
            jahr={jahr}
            monat={monat}
            dateien={zettel.dateien}
            offen={vorschlag}
            onWechsel={(id) => setVorschlag(id)}
            onSchliessen={() => setVorschlag(null)}
            nebenVorschlag
          />
          <ZettelVorschlag
            userId={userId}
            jahr={jahr}
            monat={monat}
            fileId={vorschlag}
            arten={daten.arten}
            ergebnis={ergebnis.tage}
            darfUebernehmen={darfErfassen && (!fertig || aenderbar)}
            darfLesen={darfZettelPflegen}
            aenderung={fertig ? vorgang?.id : undefined}
            onUebernommen={() => void laden()}
            onSchliessen={() => setVorschlag(null)}
          />
        </div>
      )}

      {userId && daten && (
        <AendernDialog
          isOpen={aendernOffen}
          onClose={() => setAendernOffen(false)}
          userId={userId}
          jahr={jahr}
          monat={monat}
          bezeichnung={`${nameVon(mitarbeiter.find((u) => u.id === userId) ?? { id: userId, email: null })}, ${MONATSNAMEN[monat - 1]} ${jahr}`}
          onBegonnen={(v) => {
            setAendernOffen(false);
            setVorgang(v);
          }}
        />
      )}

      {daten?.entlohnung && ergebnis && <EntlohnungHinweis entlohnung={daten.entlohnung} ergebnis={ergebnis} />}

      {daten && !fertig && <Hinweise daten={daten} aktiveArten={aktiveArten.length} />}

      {userId && daten && (
        <FertigstellenDialog
          isOpen={fertigstellenOffen}
          onClose={() => setFertigstellenOffen(false)}
          userId={userId}
          jahr={jahr}
          monat={monat}
          bezeichnung={`${nameVon(mitarbeiter.find((u) => u.id === userId) ?? { id: userId, email: null })}, ${MONATSNAMEN[monat - 1]} ${jahr}`}
          onFertig={(m) => {
            setFertigstellenOffen(false);
            uebernimm(m);
          }}
        />
      )}

      {userId && laedt && !daten && <p className="text-sm text-muted-foreground">Laden…</p>}

      {daten && ergebnis && (
        <>
          <div className={zettel.offen ? 'grid items-start gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]' : ''}>
          <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {spaltenModus && (
              <>
                <span className="mr-auto text-xs text-muted-foreground">
                  Spaltenkopf ziehen, um die Reihenfolge zu ändern · ✕ blendet aus
                </span>
                {versteckt.length > 0 && (
                  <select
                    aria-label="Spalte einblenden"
                    className="rounded border border-border bg-background px-1.5 py-1 text-xs text-foreground"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) aendereLayout(blendeEin(layout, e.target.value as RasterSpaltenKey));
                    }}
                  >
                    <option value="">+ Spalte einblenden …</option>
                    {versteckt.map((sp) => (
                      <option key={sp.key} value={sp.key}>
                        {sp.name}
                      </option>
                    ))}
                  </select>
                )}
                <LayoutDefaultActions
                  settingKey={RASTER_LAYOUT_KEY}
                  currentValue={layout}
                  onReset={(wert) => {
                    const neu = rasterLayoutAus(wert);
                    markUiSettingApplied(RASTER_LAYOUT_KEY, neu);
                    setLayout(neu);
                  }}
                />
              </>
            )}
            <button
              type="button"
              onClick={() => setSpaltenModus((v) => !v)}
              aria-pressed={spaltenModus}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                spaltenModus
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              title="Spalten ein- und ausblenden, Reihenfolge ändern"
            >
              {spaltenModus ? <Check size={13} aria-hidden="true" /> : <Settings size={13} aria-hidden="true" />}
              {spaltenModus ? 'Fertig' : 'Spalten'}
            </button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full border-collapse text-xs">
              <thead className="sticky top-0 z-10 bg-muted text-muted-foreground">
                <tr>
                  <Kopf titel="Tag" hinweis="Wochenende und Feiertage sind hinterlegt" links />
                  {spalten.map((sp) => (
                    <Kopf
                      key={sp.key}
                      titel={sp.titel}
                      hinweis={sp.hinweis}
                      links={sp.links}
                      rechnet={sp.rechnet}
                      ziel={zielSpalte === sp.key}
                      ziehen={
                        spaltenModus
                          ? {
                              beginne: () => (gezogen.current = sp.key),
                              ueber: () => setZielSpalte(sp.key),
                              ablegen: () => {
                                if (gezogen.current) aendereLayout(verschiebeSpalte(layout, gezogen.current, sp.key));
                                gezogen.current = null;
                                setZielSpalte(null);
                              },
                              ende: () => {
                                gezogen.current = null;
                                setZielSpalte(null);
                              },
                              entfernen: sp.fest ? undefined : () => aendereLayout(blendeAus(layout, sp.key)),
                            }
                          : undefined
                      }
                    />
                  ))}
                  <th className="w-6" aria-label="Status" />
                </tr>
              </thead>
              <tbody onKeyDown={rasterTaste}>
                {ergebnis.tage.map((tag) =>
                  // Kein Ersatzobjekt im Render: Die Stelle übernähme jedes neue (siehe TagZeile).
                  !zeilen[tag.datum] ? null : (
                  <TagZeile
                    key={`${userId}:${tag.datum}`}
                    zeile={zeilen[tag.datum]}
                    tag={tag}
                    feiertag={feiertage.get(tag.datum) ?? null}
                    ausserDienst={ausserDienst(tag.datum, daten)}
                    arten={daten.arten}
                    gesperrt={!darfErfassen || (fertig && !aenderbar)}
                    aenderung={fertig ? vorgang?.id : undefined}
                    onGespeichert={gespeichert}
                    spalten={spalten}
                  />
                  ),
                )}
              </tbody>
              <tfoot className="border-t-2 border-border bg-muted/60 font-semibold">
                <tr>
                  <td className="px-2 py-1.5">Summe</td>
                  {spalten.map((sp) => (
                    <Fragment key={sp.key}>
                      {sp.zulage ? zulageSumme(sp, ergebnis) : summenZelle(sp.key, ergebnis, nlzSummeOffen, () => setNlzSummeOffen((o) => !o))}
                    </Fragment>
                  ))}
                  <td />
                </tr>
                {nlzSummeOffen && (
                  <tr>
                    <td colSpan={breite} className="bg-muted/40 px-3 py-2 text-xs font-normal text-foreground">
                      NLZ im Monat {std(nlzVon(ergebnis), false)} h
                      {Object.keys(ergebnis.zaehler.nlz ?? {}).length > 0 &&
                        ` = ${Object.entries(ergebnis.zaehler.nlz)
                          .map(([k, h]) => `${k} ${std(h, false)} h`)
                          .join(' · ')}`}
                      <span className="text-muted-foreground"> — gegengerechnet zum Soll von {std(ergebnis.summen.soll, false)} h.</span>
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
          </div>
          {zettel.offen && (
            <ZettelAnsicht
              userId={userId}
              jahr={jahr}
              monat={monat}
              dateien={zettel.dateien}
              offen={zettel.offen}
              onWechsel={(id) => setZettel((z) => ({ ...z, offen: id }))}
              onSchliessen={() => setZettel((z) => ({ ...z, offen: null }))}
            />
          )}
          </div>

          <Aufschluesselung ergebnis={ergebnis} vorlage={vorlage} werte={daten.werte} />

          {fertig && (
            <Aenderungsprotokoll
              userId={userId}
              jahr={jahr}
              monat={monat}
              stand={daten.abschluss.changedAfterFinalAt}
              arten={daten.arten}
            />
          )}
        </>
      )}
    </div>
  );
}

function leererTag(userId: string, date: string): DayEntry {
  return {
    id: null,
    userId,
    date,
    origin: null,
    startTime: null,
    endTime: null,
    breakMinutes: null,
    grossOverride: null,
    netOverride: null,
    travelHours: null,
    departure: null,
    returnTime: null,
    km: null,
    isDriver: false,
    overnight: false,
    absenceTypeId: null,
    absenceScope: null,
    absences: null,
    allowances: null,
    appointment: null,
    appointmentHours: null,
    note: null,
    updatedAt: null,
  };
}

/** Vor dem Eintritt oder nach dem Austritt laut Arbeitszeitmodell. */
function ausserDienst(datum: string, daten: PayrollMonth): boolean {
  const m = daten.modell;
  if (!m) return false;
  return (m.entryDate !== null && datum < m.entryDate) || (m.exitDate !== null && datum > m.exitDate);
}

function Hinweise({ daten, aktiveArten }: { daten: PayrollMonth; aktiveArten: number }) {
  const hinweise: ReactNode[] = [];
  if (!daten.modell) {
    hinweise.push(
      <>
        Für diesen Monat ist kein Arbeitszeitmodell hinterlegt — gerechnet wird mit Soll 0, also ist jede
        Stunde Mehrarbeit.{' '}
        <Link to="/payroll/work-schedules" className="text-primary underline">
          Arbeitszeitmodell anlegen
        </Link>
      </>,
    );
  }
  if (daten.modellWechselAm) {
    hinweise.push(
      <>
        Am {daten.modellWechselAm.split('-').reverse().join('.')} beginnt ein neues Arbeitszeitmodell. Dieser
        Monat wird durchgehend mit dem vorigen gerechnet.
      </>,
    );
  }
  if (aktiveArten === 0) {
    hinweise.push(
      <>
        Es sind noch keine Fehlzeit-Arten angelegt — Urlaub oder Krankenstand lassen sich erst danach erfassen.{' '}
        <Link to="/payroll/absence-types" className="text-primary underline">
          Fehlzeit-Arten
        </Link>
      </>,
    );
  }
  if (hinweise.length === 0) return null;
  return (
    <div className="space-y-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-foreground">
      {hinweise.map((h, i) => (
        <p key={i} className="flex items-start gap-2">
          <Info size={14} className="mt-0.5 shrink-0 text-amber-600" />
          <span>{h}</span>
        </p>
      ))}
    </div>
  );
}

interface Ziehen {
  beginne: () => void;
  ueber: () => void;
  ablegen: () => void;
  ende: () => void;
  /** Fehlt bei festen Spalten. */
  entfernen?: () => void;
}

function Kopf({
  titel,
  hinweis,
  links,
  rechnet,
  ziehen,
  ziel,
}: {
  titel: string;
  hinweis?: string;
  links?: boolean;
  rechnet?: boolean;
  /** Im Spaltenmodus: Der Kopf lässt sich ziehen und (außer bei festen Spalten) ausblenden. */
  ziehen?: Ziehen;
  /** Gerade Ziel eines gezogenen Kopfs. */
  ziel?: boolean;
}) {
  return (
    <th
      scope="col"
      title={hinweis}
      draggable={ziehen ? true : undefined}
      onDragStart={ziehen?.beginne}
      onDragOver={
        ziehen
          ? (e) => {
              e.preventDefault();
              ziehen.ueber();
            }
          : undefined
      }
      onDrop={ziehen?.ablegen}
      onDragEnd={ziehen?.ende}
      className={`whitespace-nowrap px-1 py-1.5 font-semibold ${links ? 'text-left' : 'text-center'} ${
        rechnet ? 'bg-primary/5' : ''
      } ${ziehen ? 'cursor-grab select-none' : hinweis ? 'cursor-help' : ''} ${
        ziel ? 'outline outline-2 -outline-offset-2 outline-primary' : ''
      }`}
    >
      {titel}
      {ziehen?.entfernen && (
        <button
          type="button"
          onClick={ziehen.entfernen}
          className="ml-0.5 rounded px-0.5 text-muted-foreground hover:text-destructive"
          aria-label={`Spalte ${titel} ausblenden`}
          title="Spalte ausblenden"
        >
          ✕
        </button>
      )}
    </th>
  );
}

function Summe({ wert }: { wert: number }) {
  return <td className="bg-primary/5 px-1.5 text-right tabular-nums">{std(wert)}</td>;
}

/** Die Zelle der Summenzeile für eine Spalte; wo es nichts zu summieren gibt, bleibt sie leer. */
function summenZelle(key: RasterSpaltenKey, e: MonatsErgebnis, nlzOffen: boolean, nlzUmschalten: () => void): ReactNode {
  switch (key) {
    case 'km':
      return <td className="px-1 text-right tabular-nums">{e.summen.km || ''}</td>;
    case 'lenker':
      return <td className="px-1 text-center tabular-nums">{e.zaehler.lenkerTage || ''}</td>;
    case 'naechtigung':
      return <td className="px-1 text-center tabular-nums">{e.zaehler.naechtigungen || ''}</td>;
    case 'soll':
      return <Summe wert={e.summen.soll} />;
    case 'nlz':
      return (
        <td className="bg-primary/10 px-1.5 text-right tabular-nums">
          <button
            type="button"
            className="inline-flex items-center gap-0.5 hover:text-primary"
            aria-expanded={nlzOffen}
            title="NLZ des Monats nach Art"
            onClick={nlzUmschalten}
          >
            {nlzOffen ? <ChevronDown size={12} aria-hidden="true" /> : <ChevronRight size={12} aria-hidden="true" />}
            {std(nlzVon(e), false)}
          </button>
        </td>
      );
    case 'naz':
      return <Summe wert={e.summen.naz} />;
    case 'mehr':
      return <Summe wert={e.summen.mehrarbeitTeilzeit + e.summen.mehrarbeitVollzeit} />;
    case 'ues':
      return <Summe wert={e.summen.ueberstunden} />;
    case 'uesGew':
      return <Summe wert={e.summen.ueberstundenGewichtet} />;
    case 'za':
      return <Summe wert={e.summen.zaSaldo} />;
    case 'montage':
      return <Summe wert={e.summen.montagezeit} />;
    case 'entfernung':
      return (
        <td className="whitespace-nowrap bg-primary/5 px-1.5 text-right tabular-nums">
          {e.zaehler.entfernungszulageTage ? geldText(e.zaehler.entfernungszulageBetrag) : ''}
        </td>
      );
    // `?? 0`: Ein vor Teil 5 fertiggestellter Monat kennt die Trennung nicht.
    case 'wegLenker':
      return <Summe wert={e.summen.wegzeitAusserhalbLenker ?? 0} />;
    case 'wegBeifahrer':
      return <Summe wert={e.summen.wegzeitAusserhalbBeifahrer ?? 0} />;
    default:
      return <td />;
  }
}

/** Die Summenzelle einer Zulagenspalte: erfasste Stunden bzw. angehakte Tage, beim Ergebnis der Betrag. */
function zulageSumme(sp: RasterSpalte, e: MonatsErgebnis): ReactNode {
  const { def, art } = sp.zulage!;
  const z = e.zaehler.zulagen?.[def.schluessel];
  if (art === 'eingabe') {
    return def.bemessung === 'erfasste_stunden' ? <Summe wert={z?.menge ?? 0} /> : <td className="px-1.5 text-center tabular-nums">{z?.tage || ''}</td>;
  }
  return (
    <td className="bg-primary/5 px-1.5 text-right tabular-nums" title={z ? `${def.name}: ${std(z.menge, false)} ${def.einheit} an ${z.tage} ${z.tage === 1 ? 'Tag' : 'Tagen'}` : undefined}>
      {z ? (def.prozent ? std(z.menge) : geldText(z.betrag)) : ''}
    </td>
  );
}

/**
 * Stunden einer Zulage am Tag. Kein Feld der Speicherstelle: Die Tageseingaben gehen als
 * ganze Liste (wie die Fehlzeiten) — das Feld meldet beim Verlassen nur seinen neuen Wert.
 */
function ZulageStunden({ label, wert, gesperrt, onAendern }: { label: string; wert: number | null; gesperrt: boolean; onAendern: (stunden: number) => void }) {
  const [text, setText] = useState<string | null>(null);
  const gespeichert = wert ? String(wert).replace('.', ',') : '';
  const zahl = text === null || text.trim() === '' ? 0 : Number(text.replace(',', '.'));
  const ungueltig = text !== null && (!Number.isFinite(zahl) || zahl < 0 || zahl > 24);
  return (
    <input
      aria-label={label}
      aria-invalid={ungueltig || undefined}
      title={ungueltig ? 'Stunden 0–24' : undefined}
      inputMode="decimal"
      className={eingabe}
      disabled={gesperrt}
      value={text ?? gespeichert}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setText(null);
      }}
      onBlur={() => {
        if (text === null || ungueltig) return;
        setText(null);
        if (zahl !== (wert ?? 0)) onAendern(Math.round(zahl * 100) / 100);
      }}
    />
  );
}

// ── Eine Tageszeile ─────────────────────────────────────────────────────────

const eingabe =
  'w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-center tabular-nums hover:border-input focus:border-input focus:bg-background focus:outline-none focus:ring-1 focus:ring-ring aria-[invalid=true]:border-destructive disabled:cursor-default disabled:hover:border-transparent';

function TagZeile({
  zeile,
  tag,
  feiertag,
  ausserDienst,
  arten,
  gesperrt,
  aenderung,
  onGespeichert,
  spalten,
}: {
  /** Die gezeigten Spalten in ihrer Reihenfolge (AP-22 Teil 5). */
  spalten: RasterSpalte[];
  /** Kennung des Änderungsvorgangs, wenn der Lohnmonat fertiggestellt ist (G2). */
  aenderung?: string;
  zeile: DayEntry;
  tag: TagErgebnis;
  feiertag: string | null;
  ausserDienst: boolean;
  arten: PayrollMonth['arten'];
  gesperrt: boolean;
  onGespeichert: (z: DayEntry) => void;
}) {
  const pfad = `/payroll/day-entries/${zeile.userId}/${zeile.date}${aenderung ? `?change=${aenderung}` : ''}`;
  // Stabil halten: Die Stelle übernimmt jedes neue Objekt, und ein leerer Tag hat
  // keinen Stand, an dem sie ein gleiches erkennen könnte — ein frisches Objekt
  // je Render wäre eine Endlosschleife.
  const daten = useMemo(() => ({ data: zeile }), [zeile]);
  const stelle = useSpeicherstelle<{ data: DayEntry }>({
    schluessel: `payrollDay:${zeile.userId}:${zeile.date}`,
    bezeichnung: `Tag ${zeile.date.split('-').reverse().join('.')}`,
    daten,
    schreibe: (patch, stand) =>
      api.patch<{ data: DayEntry }>(pfad, patch, mitStand(stand)).then((r) => {
        onGespeichert(r.data.data);
        return r.data;
      }),
    keepalive: { url: pfad, method: 'PATCH' },
  }) as Speicherstelle<unknown>;

  const uhr = { art: 'text' as const, lies: leseUhrzeit, pruefe: pruefeUhrzeit, gesperrt };
  const kommt = useFeld<string | null>(stelle, 'startTime', { ...uhr, label: 'Kommt' });
  const geht = useFeld<string | null>(stelle, 'endTime', { ...uhr, label: 'Geht' });
  const abfahrt = useFeld<string | null>(stelle, 'departure', { ...uhr, label: 'Abfahrt' });
  const rueckfahrt = useFeld<string | null>(stelle, 'returnTime', { ...uhr, label: 'Rückfahrt' });
  const ganzzahl = (max: number) => (w: number | null) =>
    w === null || (Number.isInteger(w) && w >= 0 && w <= max) ? null : `Ganze Zahl 0–${max}`;
  const stunden = (w: number | null) => (w === null || (w >= 0 && w <= 24) ? null : 'Stunden 0–24');
  const pause = useFeld<number | null>(stelle, 'breakMinutes', { art: 'zahl', stellen: 0, pruefe: ganzzahl(1440), label: 'Pause', gesperrt });
  const reise = useFeld<number | null>(stelle, 'travelHours', { art: 'zahl', stellen: 2, pruefe: stunden, label: 'Reisezeit', gesperrt });
  const km = useFeld<number | null>(stelle, 'km', { art: 'zahl', stellen: 0, pruefe: ganzzahl(5000), label: 'km', gesperrt });
  const lenker = useFeld<boolean>(stelle, 'isDriver', { art: 'schalter', label: 'Lenker', gesperrt });
  const naechtigung = useFeld<boolean>(stelle, 'overnight', { art: 'schalter', label: 'Nächtigung', gesperrt });
  const termin = useFeld<string | null>(stelle, 'appointment', { label: 'Termin', gesperrt });
  const terminStd = useFeld<number | null>(stelle, 'appointmentHours', { art: 'zahl', stellen: 2, pruefe: stunden, label: 'Termindauer', gesperrt });

  const felder: Feld<unknown>[] = [kommt, geht, pause, reise, abfahrt, rueckfahrt, km, lenker, naechtigung, termin, terminStd] as Feld<unknown>[];
  const status = zeilenStatus(felder);

  // ── Fehlzeiten in Stunden (AP-22 Teil 4) ──────────────────────────────────
  // Die Liste ist ein Feld der Tageszeile und geht als Ganzes — über dieselbe Stelle,
  // also mit Stand, Warteschlange und Protokoll wie jedes andere Feld des Tages.
  const [offen, setOffen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const stand = useStellenDaten(stelle) as Partial<DayEntry>;
  const artVon = useMemo(() => new Map(arten.map((a) => [a.id, a])), [arten]);
  // Bestand (ganz/halb) erscheint mit seinen Stunden; wer ihn ändert, schreibt Stunden.
  const fehlzeiten: { absenceTypeId: string; hours: number }[] = stand.absences?.length
    ? stand.absences
    : stand.absenceTypeId
      ? [{ absenceTypeId: stand.absenceTypeId, hours: Math.round(tag.soll * (stand.absenceScope === 'HALF' ? 0.5 : 1) * 100) / 100 }]
      : [];
  const schreibeFehlzeiten = async (liste: { absenceTypeId: string; hours: number }[]) => {
    setFehler(null);
    const r = await stelle.sende({ kennung: 'absences', felder: ['absences'], labels: { absences: 'Fehlzeiten' }, patch: { absences: liste } });
    if (r.art === 'fehler') setFehler(r.text);
  };
  // ── Tageseingaben der Zulagen (AP-22 Teil 6, K3) ──────────────────────────
  // Wie die Fehlzeiten ein Feld der Tageszeile, das als Ganzes geht — über dieselbe Stelle.
  const [zulagenFehler, setZulagenFehler] = useState<string | null>(null);
  const eingaben: Record<string, number | boolean> = stand.allowances ?? {};
  const schreibeZulage = async (key: string, wert: number | boolean) => {
    setZulagenFehler(null);
    const neu = { ...eingaben };
    if (wert) neu[key] = wert;
    else delete neu[key];
    const r = await stelle.sende({
      kennung: 'allowances',
      felder: ['allowances'],
      labels: { allowances: 'Zulagen' },
      patch: { allowances: Object.keys(neu).length ? neu : null },
    });
    if (r.art === 'fehler') setZulagenFehler(r.text);
  };
  const kennzeichen = (key: string, label: string) => (
    <Zelle breite="w-8">
      <input
        type="checkbox"
        aria-label={label}
        className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
        checked={eingaben[key] === true}
        disabled={gesperrt}
        onChange={(e) => void schreibeZulage(key, e.target.checked)}
      />
    </Zelle>
  );
  /** Die Zelle einer Zulagenspalte: Eingabe (Stunden oder Haken) oder Ergebnis (Betrag bzw. Stunden). */
  const zulageZelle = (sp: RasterSpalte): ReactNode => {
    const { def, art } = sp.zulage!;
    if (art === 'eingabe') {
      if (def.bemessung === 'markierte_tage_naz') return kennzeichen(def.schluessel, def.name);
      const w = eingaben[def.schluessel];
      return (
        <Zelle breite="w-12">
          <ZulageStunden
            label={`${def.name} in Stunden`}
            wert={typeof w === 'number' ? w : null}
            gesperrt={gesperrt}
            onAendern={(n) => void schreibeZulage(def.schluessel, n)}
          />
        </Zelle>
      );
    }
    const z = tag.zulagen?.find((x) => x.schluessel === def.schluessel);
    return (
      <td className="whitespace-nowrap bg-primary/5 px-1.5 text-right tabular-nums" title={z ? `${def.name}: ${std(z.menge, false)} ${def.einheit}` : undefined}>
        {z ? (def.prozent ? std(z.menge) : geldText(z.betrag)) : ''}
      </td>
    );
  };

  const gutschrift = fehlzeiten.reduce((s, a) => s + (artVon.get(a.absenceTypeId)?.countsAsTarget ? a.hours : 0), 0);
  // Vorschlag: ohne Arbeitszeit das Tagessoll, sonst der Rest auf das Soll.
  const rest = Math.round(Math.max(0, tag.soll - tag.naz - gutschrift) * 100) / 100;
  const vorschlag = rest > 0 ? rest : tag.soll > 0 && fehlzeiten.length === 0 ? tag.soll : 0;
  const frei_ = arten.filter((a) => a.isActive && !fehlzeiten.some((f) => f.absenceTypeId === a.id));
  const nlz = tag.nlz ?? tag.abwesenheitStunden + (tag.feiertagBezahlt ? tag.soll : 0);
  const ohneNlz = fehlzeiten.filter((f) => !artVon.get(f.absenceTypeId)?.paid).map((f) => artVon.get(f.absenceTypeId)?.code ?? '?');
  const fehlt = tag.soll > 0 ? tag.minderstunden : 0;
  const frei = tag.istWochenende || tag.istFeiertag;
  const hintergrund = ausserDienst ? 'bg-muted/60 text-muted-foreground' : frei ? 'bg-muted/40' : '';
  const titel = [feiertag, ausserDienst ? 'außerhalb der Beschäftigung laut Arbeitszeitmodell' : null]
    .filter(Boolean)
    .join(' · ');

  // Bestand: Eine leere Pause, bei der die Regel greift, steht grau im Feld (AP-22 Teil 5) —
  // neu erfasste Tage tragen sie als Wert.
  const pauseGerechnet = pause.wert === null && tag.pausenabzug > 0 ? String(Math.round(tag.pausenabzug * 60)) : undefined;

  // Eine Zelle je Spalte; gezeichnet werden die gezeigten, in der Reihenfolge des Benutzers.
  const zellen: Record<RasterSpaltenKey, ReactNode> = {
    rueckruf: kennzeichen('rueckruf', 'Rückruf'),
    verpflegung: kennzeichen('verpflegung', 'Verpflegung gestellt'),
    kommt: <Zelle breite="w-14"><input aria-label="Kommt" className={eingabe} {...kommt.props} /></Zelle>,
    geht: <Zelle breite="w-14"><input aria-label="Geht" className={eingabe} {...geht.props} /></Zelle>,
    pause: (
      <Zelle breite="w-12">
        <input
          aria-label="Pause in Minuten"
          inputMode="numeric"
          placeholder={pauseGerechnet}
          title={pauseGerechnet ? `Pausenregel: ${pauseGerechnet} min werden abgezogen` : undefined}
          className={eingabe}
          {...pause.props}
        />
      </Zelle>
    ),
    reise: <Zelle breite="w-12"><input aria-label="Reisezeit" inputMode="decimal" className={eingabe} {...reise.props} /></Zelle>,
    abfahrt: <Zelle breite="w-14"><input aria-label="Abfahrt" className={eingabe} {...abfahrt.props} /></Zelle>,
    rueckfahrt: <Zelle breite="w-14"><input aria-label="Rückfahrt" className={eingabe} {...rueckfahrt.props} /></Zelle>,
    km: <Zelle breite="w-12"><input aria-label="Kilometer" inputMode="numeric" className={eingabe} {...km.props} /></Zelle>,
    lenker: (
      <Zelle breite="w-8">
        <input type="checkbox" aria-label="Lenker" className="h-3.5 w-3.5 accent-[hsl(var(--primary))]" {...lenker.schalter} />
      </Zelle>
    ),
    naechtigung: (
      <Zelle breite="w-8">
        <input type="checkbox" aria-label="Nächtigung" className="h-3.5 w-3.5 accent-[hsl(var(--primary))]" {...naechtigung.schalter} />
      </Zelle>
    ),
    termin: (
      <td className="px-1 py-0.5">
        <div className="flex items-center gap-1">
          <input aria-label="Termin" placeholder={gesperrt ? '' : '—'} className={`${eingabe} w-20 text-left`} {...termin.props} />
          {(termin.wert || terminStd.wert !== null) && (
            <input aria-label="Termindauer in Stunden" inputMode="decimal" className={`${eingabe} w-12`} {...terminStd.props} />
          )}
        </div>
      </td>
    ),
    soll: <Rechnet wert={tag.soll} />,
    nlz: (
      <td className="whitespace-nowrap bg-primary/10 px-1 text-right tabular-nums">
        <button
          type="button"
          className="inline-flex w-full items-center justify-end gap-0.5 rounded px-0.5 hover:text-primary focus:outline-none focus:ring-1 focus:ring-ring"
          aria-expanded={offen}
          aria-label={`NLZ ${zeile.date.slice(8, 10)}. — Aufschlüsselung ${offen ? 'schließen' : 'öffnen'}`}
          title={fehlt > 0 ? `${std(fehlt, false)} h fehlen auf das Soll` : 'Woher die NLZ kommt — hier erfasst du Fehlzeiten in Stunden'}
          onClick={() => setOffen((o) => !o)}
        >
          {offen ? <ChevronDown size={12} aria-hidden="true" /> : <ChevronRight size={12} className="opacity-40" aria-hidden="true" />}
          {ohneNlz.length > 0 && <span className="text-[10px] text-muted-foreground">{ohneNlz.join(' ')}</span>}
          <span>{std(nlz)}</span>
        </button>
      </td>
    ),
    naz: <Rechnet wert={tag.naz} />,
    mehr: <Rechnet wert={tag.mehrarbeitTeilzeit + tag.mehrarbeitVollzeit} />,
    ues: <Rechnet wert={tag.ueberstunden} />,
    uesGew: <Rechnet wert={tag.ueberstundenGewichtet} />,
    // An einem Tag ohne Erfassung steht kein Minus in Höhe des Solls (Auftraggeber 4.10.2026) —
    // nur die Anzeige: Die Summe unten rechnet weiter mit dem ganzen Monat.
    za: <Rechnet wert={zeile.id === null ? 0 : tag.zaTag} />,
    montage: <Rechnet wert={tag.montagezeit} />,
    entfernung: (
      <td
        className="whitespace-nowrap bg-primary/5 px-1.5 text-right tabular-nums"
        title={tag.entfernungStufe ? `Entfernungszulage ${STUFE[tag.entfernungStufe]}` : undefined}
      >
        {tag.entfernungStufe ? geldText(tag.entfernungBetrag) : ''}
      </td>
    ),
    // `?? 0`: Ein vor Teil 5 fertiggestellter Monat kennt die Trennung nicht.
    wegLenker: <Rechnet wert={tag.wegzeitAusserhalbLenker ?? 0} />,
    wegBeifahrer: <Rechnet wert={tag.wegzeitAusserhalbBeifahrer ?? 0} />,
  };

  return (
    <>
    <tr className={`border-t border-border/60 ${hintergrund}`}>
      <td className="whitespace-nowrap px-2 py-0.5 tabular-nums" title={titel || undefined}>
        <span className={frei ? 'font-semibold text-primary' : ''}>{KURZ_TAGE[tag.wochentag % 7]}</span>{' '}
        {zeile.date.slice(8, 10)}.
        {feiertag && <span className="ml-1 text-[10px] text-primary">F</span>}
      </td>
      {spalten.map((sp) => (
        <Fragment key={sp.key}>{sp.zulage ? zulageZelle(sp) : zellen[sp.key]}</Fragment>
      ))}
      <td className="w-6 px-1">
        {(status.fehler ?? zulagenFehler) ? (
          <span title={status.fehler ?? zulagenFehler ?? undefined} className="flex text-destructive">
            <AlertCircle size={14} aria-label={`Fehler: ${status.fehler ?? zulagenFehler}`} />
          </span>
        ) : (
          <FeldStatusZeichen status={status} />
        )}
      </td>
    </tr>
    {offen && (
      <tr className="border-t border-border/40 bg-muted/40">
        <td colSpan={spalten.length + 2} className="px-3 py-2">
          <div className="space-y-1.5">
            {tag.feiertagBezahlt && (
              <p className="text-xs text-foreground">
                Gesetzlicher Feiertag{feiertag ? ` (${feiertag})` : ''} — {std(tag.soll, false)} h automatisch aus dem Feiertagskalender.{' '}
                <span className="text-muted-foreground">Wer am Feiertag arbeitet, trägt Kommt und Geht ein.</span>
              </p>
            )}
            {(tag.halbtagGutschrift ?? 0) > 0 && (
              <p className="text-xs text-foreground">
                24./31. Dezember — {std(tag.halbtagGutschrift ?? 0, false)} h fortgezahlt: Die Arbeitszeit endet vorgezogen.{' '}
                <span className="text-muted-foreground">Wer länger arbeitet, bekommt auf diese Stunden den Zuschlag statt der Gutschrift.</span>
              </p>
            )}
            {fehlzeiten.map((f) => {
              const a = artVon.get(f.absenceTypeId);
              return (
                <div key={f.absenceTypeId} className="flex flex-wrap items-center gap-2 text-xs">
                  <select
                    aria-label="Fehlzeit-Art"
                    className={`${klein} w-56`}
                    disabled={gesperrt}
                    value={f.absenceTypeId}
                    onChange={(e) =>
                      void schreibeFehlzeiten(fehlzeiten.map((x) => (x === f ? { ...x, absenceTypeId: e.target.value } : x)))
                    }
                  >
                    {arten
                      .filter((x) => x.id === f.absenceTypeId || (x.isActive && !fehlzeiten.some((y) => y.absenceTypeId === x.id)))
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.code} · {x.name}
                        </option>
                      ))}
                  </select>
                  <StundenFeld
                    wert={f.hours}
                    gesperrt={gesperrt}
                    onAendern={(h) => void schreibeFehlzeiten(fehlzeiten.map((x) => (x === f ? { ...x, hours: h } : x)))}
                  />
                  <span className="text-muted-foreground">
                    h · {a?.paid ? 'bezahlt' : 'unbezahlt'}
                    {a?.timeCompensation ? ' · vom Zeitausgleich' : a?.countsAsTarget ? ' · zählt als Soll' : ' · zählt nicht als Soll'}
                    {tag.soll > 0 && f.hours > tag.soll ? ' · mehr als das Tagessoll' : ''}
                  </span>
                  {!gesperrt && (
                    <button
                      type="button"
                      className="rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      aria-label="Fehlzeit entfernen"
                      title="Entfernen"
                      onClick={() => void schreibeFehlzeiten(fehlzeiten.filter((x) => x !== f))}
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              );
            })}
            {!gesperrt && frei_.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Plus size={13} className="text-muted-foreground" aria-hidden="true" />
                <select
                  aria-label="Fehlzeit hinzufügen"
                  className={`${klein} w-56`}
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    void schreibeFehlzeiten([...fehlzeiten, { absenceTypeId: e.target.value, hours: vorschlag > 0 ? vorschlag : 1 }]);
                  }}
                >
                  <option value="">Fehlzeit hinzufügen …</option>
                  {frei_.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.code} · {x.name}
                    </option>
                  ))}
                </select>
                {vorschlag > 0 && (
                  <span className="text-muted-foreground">
                    Vorschlag: {std(vorschlag, false)} h{tag.naz > 0 || fehlzeiten.length > 0 ? ' (Rest auf das Soll)' : ' (Tagessoll)'}
                  </span>
                )}
              </div>
            )}
            {!gesperrt && arten.every((a) => !a.isActive) && (
              <p className="text-xs text-muted-foreground">Es sind keine Fehlzeit-Arten angelegt.</p>
            )}
            {(fehlzeiten.length > 0 || tag.feiertagBezahlt || (tag.halbtagGutschrift ?? 0) > 0) && (
              <p className="text-xs text-muted-foreground">
                NLZ {std(nlz, false)} h
                {fehlzeiten.filter((f) => artVon.get(f.absenceTypeId)?.paid).length > 0 &&
                  ` = ${fehlzeiten
                    .filter((f) => artVon.get(f.absenceTypeId)?.paid)
                    .map((f) => `${artVon.get(f.absenceTypeId)?.name ?? '?'} ${std(f.hours, false)} h`)
                    .join(' + ')}`}
                {' · '}gearbeitet {std(tag.naz, false)} h · Soll {std(tag.soll, false)} h
                {tag.soll > 0 && (
                  <span className={fehlt > 0 ? ' text-amber-700 dark:text-amber-300' : ' text-green-700 dark:text-green-400'}>
                    {' · '}
                    {fehlt > 0 ? `${std(fehlt, false)} h fehlen` : 'Soll erfüllt'}
                  </span>
                )}
              </p>
            )}
            {fehler && <p className="text-xs text-destructive">{fehler}</p>}
          </div>
        </td>
      </tr>
    )}
    </>
  );
}

/** Felder der Aufschlüsselung: sichtbar umrandet und mit eigener Breite — `eingabe` füllt die Zelle. */
const klein =
  'rounded border border-input bg-background px-1.5 py-0.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-default disabled:opacity-70';

/** NLZ des Monats; ein vor Teil 4 eingefrorener Monat trägt sie noch nicht. */
function nlzVon(e: MonatsErgebnis): number {
  return e.summen.nlz ?? e.tage.reduce((s, t) => s + (t.nlz ?? t.abwesenheitStunden + (t.feiertagBezahlt ? t.soll : 0)), 0);
}

/** Stunden einer Fehlzeit: übernommen wird beim Verlassen, Esc stellt den Wert wieder her. */
function StundenFeld({ wert, gesperrt, onAendern }: { wert: number; gesperrt: boolean; onAendern: (h: number) => void }) {
  const zeige = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const [text, setText] = useState(zeige(wert));
  const [ungueltig, setUngueltig] = useState(false);
  useEffect(() => {
    setText(zeige(wert));
    setUngueltig(false);
  }, [wert]);
  return (
    <input
      aria-label="Stunden"
      inputMode="decimal"
      className={`${klein} w-16 text-right tabular-nums aria-[invalid=true]:border-destructive`}
      aria-invalid={ungueltig}
      disabled={gesperrt}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setText(zeige(wert));
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      onBlur={() => {
        const h = leseZahl(text);
        if (h === null || h < 0 || h > 24) return setUngueltig(true);
        setUngueltig(false);
        const gerundet = Math.round(h * 100) / 100;
        if (gerundet !== wert) onAendern(gerundet);
        else setText(zeige(wert));
      }}
    />
  );
}

function Zelle({ breite, children }: { breite: string; children: ReactNode }) {
  return <td className={`${breite} px-0.5 py-0.5 text-center`}>{children}</td>;
}

function Rechnet({ wert }: { wert: number }) {
  return <td className="bg-primary/5 px-1.5 text-right tabular-nums">{std(wert)}</td>;
}

// ── Aufschlüsselung des Monats ──────────────────────────────────────────────

function Aufschluesselung({
  ergebnis,
  vorlage,
  werte,
}: {
  ergebnis: MonatsErgebnis;
  /** Die KV-Vorlage des Monats — für die Namen und Sätze der Zulagen (Teil 6, K3). */
  vorlage: KvVorlage | null;
  werte: Record<string, string>;
}) {
  const { summen: s, zaehler: z } = ergebnis;
  // Zulagen in der Reihenfolge des Katalogs: Menge × Satz = Betrag (bei Prozent vom Lohn nur die Stunden).
  const zulagen = (vorlage?.zulagen ?? []).flatMap((def) => {
    const m = z.zulagen?.[def.schluessel];
    return m ? [{ def, m, satz: Number(werte[zulageSchluessel(def.schluessel).satz] ?? 0) }] : [];
  });
  const zuschlaege = Object.entries(s.zuschlagJeSatz).sort(([a], [b]) => Number(a) - Number(b));
  const abwesenheiten = Object.entries(z.abwesenheitTage);

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Block titel="Stunden">
        <Wert label="Normalarbeitszeit (Soll)" wert={std(s.soll, false)} />
        <Wert label="Netto-Arbeitszeit" wert={std(s.naz, false)} />
        <Wert label="Normalstunden" wert={std(s.normalStunden, false)} />
        <Wert label="Mehrarbeit Teilzeit" wert={std(s.mehrarbeitTeilzeit, false)} />
        <Wert label="Mehrarbeit 38,5–40 h" wert={std(s.mehrarbeitVollzeit, false)} />
        <Wert label="Überstunden (roh / gewichtet)" wert={`${std(s.ueberstunden, false)} / ${std(s.ueberstundenGewichtet, false)}`} />
        <Wert label="Minderstunden" wert={std(s.minderstunden, false)} />
        <Wert label="ZA-Saldo des Monats" wert={std(s.zaSaldo, false)} />
        <Wert label="ZA verbraucht" wert={std(s.zaKonsumiert, false)} />
      </Block>
      <Block titel="Zuschläge und Montage">
        {zuschlaege.length === 0 && <Wert label="Zuschlagsstunden" wert="—" />}
        {zuschlaege.map(([satz, w]) => (
          <Wert key={satz} label={`Zuschlag ${satz} % (roh / gewichtet)`} wert={`${std(w.stunden, false)} / ${std(w.gewichtet, false)}`} />
        ))}
        <Wert label="Reisezeit" wert={std(s.reisezeit, false)} />
        <Wert label="Montagezeit" wert={std(s.montagezeit, false)} />
        <Wert label="Montagezulage (Tage / Menge)" wert={`${z.montagezulageTage} / ${std(z.montagezulageBetrag, false)}`} />
        <Wert label="Wegzeit außerhalb Betriebszeit" wert={std(s.wegzeitAusserhalb, false)} />
        {s.wegzeitAusserhalbLenker !== undefined && (
          <>
            <Wert label="— davon als Lenker" wert={std(s.wegzeitAusserhalbLenker, false)} />
            <Wert label="— davon als Beifahrer" wert={std(s.wegzeitAusserhalbBeifahrer, false)} />
          </>
        )}
      </Block>
      <Block titel="Tage">
        <Wert label="NLZ gesamt (Stunden)" wert={std(nlzVon(ergebnis), false)} />
        <Wert label="Feiertage" wert={String(z.feiertagTage)} />
        {abwesenheiten.map(([kuerzel, tage]) => (
          <Wert
            key={kuerzel}
            label={`${kuerzel} (Tage / Stunden)`}
            wert={`${tage.toLocaleString('de-DE')} / ${std(z.abwesenheitStunden[kuerzel] ?? 0, false)}`}
          />
        ))}
        <Wert label="Lenkertage" wert={String(z.lenkerTage)} />
        <Wert label="Nächtigungen" wert={String(z.naechtigungen)} />
        <Wert
          label="Entfernungszulage klein / mittel / groß"
          wert={`${z.entfernungTage.klein} / ${z.entfernungTage.mittel} / ${z.entfernungTage.gross}`}
        />
        <Wert label="Entfernungszulage Betrag" wert={geldText(z.entfernungszulageBetrag) || '—'} />
        <Wert label="Kilometer" wert={String(s.km)} />
      </Block>
      {zulagen.length > 0 && (
        <Block titel="Zulagen">
          {zulagen.map(({ def, m, satz }) => (
            <Wert
              key={def.schluessel}
              label={`${def.name} — ${std(m.menge, false)} ${def.einheit}${
                def.prozent ? ` mit ${satz.toLocaleString('de-DE')} % vom Lohn` : ` × ${satz.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`
              }`}
              wert={def.prozent ? '—' : geldText(m.betrag)}
            />
          ))}
        </Block>
      )}
      {s.rahmen && (
        // Teil 7: Stundenrahmen — je Woche bzw. Monat, ob er erfüllt ist.
        <Block
          titel={`Stundenrahmen ${std(s.rahmen.stunden, false)} h je ${s.rahmen.zeitraum === 'monat' ? 'Monat' : 'Woche'}${
            s.rahmen.mehrzeit ? '' : ' (nur Kontrolle)'
          }`}
        >
          {s.rahmen.perioden.map((p) => (
            <Wert
              key={p.von}
              label={
                s.rahmen?.zeitraum === 'monat'
                  ? 'Ist / Soll'
                  : `${p.von.slice(8, 10)}.${p.von.slice(5, 7)}.–${p.bis.slice(8, 10)}.${p.bis.slice(5, 7)}.`
              }
              wert={
                p.abgeschlossen
                  ? `${std(p.ist, false)} / ${std(p.soll, false)} (${p.differenz > 0 ? '+' : ''}${std(p.differenz, false)})`
                  : `${std(p.ist, false)} — läuft noch`
              }
            />
          ))}
        </Block>
      )}
    </div>
  );
}

function Block({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="mb-2 text-sm font-semibold text-foreground">{titel}</p>
      <dl className="space-y-1 text-sm">{children}</dl>
    </div>
  );
}

function Wert({ label, wert }: { label: string; wert: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums text-foreground">{wert}</dd>
    </div>
  );
}

function h(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Die Entlohnungsart des Monats (AP-22 Teil 3) — und was sie mit den Stunden
 * macht. Die Zahlen des Rasters bleiben, wie gerechnet; hier steht, was davon
 * mit der Pauschale abgegolten ist.
 */
function EntlohnungHinweis({ entlohnung, ergebnis }: { entlohnung: NonNullable<PayrollMonth['entlohnung']>; ergebnis: MonatsErgebnis }) {
  const e = ergebnis.entlohnung;
  const rest = e?.rest;
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-4 py-2.5 text-sm text-foreground">
      <span className="font-medium">Entlohnungsart: {entlohnung.name}</span>
      {entlohnung.art === 'KV' && <span className="text-muted-foreground"> — gerechnet wird mit den Einstellungen dieser Entlohnungsart.</span>}
      {entlohnung.modus === 'FEHLZEITEN' && (
        <span className="text-muted-foreground">
          {' '}
          — pauschal, ohne Stunden. Erfasst werden nur Fehlzeiten; der Monat ist auch ohne Tageszeiten vollständig, Mehrarbeit,
          Überstunden und Zeitausgleich gibt es nicht.
        </span>
      )}
      {entlohnung.modus === 'KONTROLLE' && (
        <span className="text-muted-foreground">
          {' '}
          — pauschal. Die Stunden werden zur Kontrolle gerechnet; Mehrarbeit und Überstunden ({h(e?.abgegolten ?? 0)} h) sind mit der
          Pauschale abgegolten und gehen nicht nach BMD.
        </span>
      )}
      {entlohnung.modus === 'UEBERSTUNDEN' && rest && (
        <span className="text-muted-foreground">
          {' '}
          — Überstundenpauschale {h(entlohnung.inklusivStunden ?? 0)} h je Monat. Abgegolten: {h(e?.abgegolten ?? 0)} h · Rest: Mehrarbeit{' '}
          {h(rest.mehrarbeitTeilzeit + rest.mehrarbeitVollzeit)} h, Überstunden {h(rest.ueberstunden)} h · ZA-Saldo {h(rest.zaSaldo)} h.
        </span>
      )}
    </div>
  );
}
